import React, { useState } from 'react';
import { MapContainer, TileLayer, ZoomControl, Polygon, Polyline, CircleMarker, Tooltip } from 'react-leaflet';
import Topbar from "../../component/Topbar";
import { 
  LuMapPin, LuPencilLine, LuTrash2, LuCalendar, 
  LuChevronDown, LuChevronUp, LuEye, LuEyeOff,
  LuChevronLeft, LuChevronRight 
} from "react-icons/lu";

import 'leaflet/dist/leaflet.css';
import "../../asset/css/report.css";

const Report = () => {
  const centerPosition = [42.3314, -83.0458];
  const [isAcres, setIsAcres] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  const [openFeatures, setOpenFeatures] = useState({ 
    lawn: true, sidewalk: false, tree: true, softedge: false 
  });

  // Added 'zones' to state
  const [zones, setZones] = useState([
    { id: 'z-01', name: 'Commercial Zone', color: '#FFA500', isVisible: true, coords: [[42.340, -83.055], [42.345, -83.050], [42.340, -83.045]] },
    { id: 'z-02', name: 'Residential Area', color: '#8A2BE2', isVisible: true, coords: [[42.325, -83.040], [42.330, -83.035], [42.325, -83.030]] },
  ]);

  const [features, setFeatures] = useState({
    multipolygon: [
      {
        id: 'lawn',
        name: 'Lawn Coverage',
        color: '#7CFF01',
        totalSqFt: 86093,
        isVisible: true,
        children: [
          { id: 563, val: 33493, isVisible: true, coords: [[42.335, -83.050], [42.338, -83.045], [42.335, -83.040], [42.332, -83.045]] },
          { id: 564, val: 27746, isVisible: true, coords: [[42.330, -83.040], [42.332, -83.038], [42.330, -83.035], [42.328, -83.037]] },
        ]
      },
      { 
        id: 'sidewalk', 
        name: 'Sidewalks', 
        color: '#FFD700', 
        totalSqFt: 12400, 
        isVisible: true,
        children: [
          { id: 901, val: 12400, isVisible: true, coords: [[42.331, -83.048], [42.331, -83.042], [42.330, -83.042], [42.330, -83.048]] }
        ] 
      }
    ],
    points: [
      { 
        id: 'tree', 
        name: 'Tree Survey', 
        color: '#00B67A', 
        count: 3, 
        isVisible: true,
        children: [
          { id: 'T-01', type: 'Oak', isVisible: true, coords: [42.334, -83.045] },
          { id: 'T-02', type: 'Maple', isVisible: true, coords: [42.3345, -83.046] },
          { id: 'T-03', type: 'Pine', isVisible: true, coords: [42.3335, -83.044] },
        ]
      }
    ],
    multilinestring: [
      { 
        id: 'softedge', 
        name: 'Boundary Lines', 
        color: '#FF5A5F', 
        length: 664.6, 
        isVisible: true,
        children: [
          { id: 'L-10', val: 664.6, isVisible: true, coords: [[42.337, -83.052], [42.339, -83.048], [42.340, -83.040]] }
        ]
      }
    ]
  });

  const toggleSection = (id) => setOpenFeatures(prev => ({ ...prev, [id]: !prev[id] }));

  const toggleVisibility = (category, id) => {
    setFeatures(prev => ({
      ...prev,
      [category]: prev[category].map(item => 
        item.id === id ? { ...item, isVisible: !item.isVisible } : item
      )
    }));
  };

  const toggleZoneVisibility = (id) => {
    setZones(prev => prev.map(zone => zone.id === id ? { ...zone, isVisible: !zone.isVisible } : zone));
  };

  const toggleItemVisibility = (category, groupId, itemId) => {
    setFeatures(prev => ({
      ...prev,
      [category]: prev[category].map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            children: group.children.map(child => 
              child.id === itemId ? { ...child, isVisible: !child.isVisible } : child
            )
          };
        }
        return group;
      })
    }));
  };

  const formatArea = (sqft) => isAcres ? `${(sqft / 43560).toFixed(2)} ac` : `${sqft.toLocaleString()} sq ft`;

  return (
    <div className="report-page">
      <Topbar />
      <main className={`report-container ${!isSidebarOpen ? 'sidebar-collapsed' : ''}`}>
        
        <button 
          className="sidebar-toggle-btn" 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? <LuChevronLeft /> : <LuChevronRight />}
        </button>

        <aside className={`data-sidebar ${!isSidebarOpen ? 'hidden' : ''}`}>
          <div className="sidebar-header">
            <div className="header-top">
              <h3>GIS Report</h3>
              <div className="unit-toggle">
                <button className={!isAcres ? "active" : ""} onClick={() => setIsAcres(false)}>Sq Ft</button>
                <button className={isAcres ? "active" : ""} onClick={() => setIsAcres(true)}>Acres</button>
              </div>
            </div>
          </div>

          <div className="sidebar-scroll-area">
            {/* PROJECT CARD */}
            <div className="project-details-card">
              <div className="card-glass-overlay"></div>
              <div className="card-content">
                <div className="card-header">
                  <span className="badge">Property ID</span> 
                </div>
                <h2 className="prop-id">DET-PRO-48216-MS</h2>
                <div className="info-row">
                  <div className="info-pill"><LuMapPin /> <span>Detroit, MI</span></div>
                  <div className="info-pill"><LuCalendar /> <span>Jan 2026</span></div>
                </div>
              </div>
            </div>

            {/* ZONES SECTION (New) */}
            <div className="inventory-section">
              <div className="section-label">Zones</div>
              <div className="category-group">
                {zones.map(zone => (
                  <div key={zone.id} className="feature-box">
                    <div className="feature-main">
                      <div className="feature-left">
                        <div className="swatch" style={{ background: zone.color }}></div>
                        <span className="f-name">{zone.name}</span>
                      </div>
                      <div className="feature-right">
                        <button 
                          className={`vis-toggle ${!zone.isVisible ? 'hidden' : ''}`}
                          onClick={() => toggleZoneVisibility(zone.id)}
                        >
                          {zone.isVisible ? <LuEye size={18} /> : <LuEyeOff size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SPATIAL LAYERS SECTION */}
            <div className="inventory-section">
              <div className="section-label">Spatial Layers</div>
              {Object.entries(features).map(([category, items]) => (
                <div className="category-group" key={category}>
                  <label className="group-title">{category.toUpperCase()}</label>
                  {items.map(group => (
                    <div key={group.id} className={`feature-box ${openFeatures[group.id] ? 'expanded' : ''}`}>
                      <div className="feature-main">
                        <div className="feature-left" onClick={() => toggleSection(group.id)}>
                          <div className="swatch" style={{ background: group.color }}></div>
                          <span className="f-name">{group.name}</span>
                        </div>
                        <div className="feature-right">
                          <button 
                            className={`vis-toggle ${!group.isVisible ? 'hidden' : ''}`}
                            onClick={() => toggleVisibility(category, group.id)}
                          >
                            {group.isVisible ? <LuEye size={18} /> : <LuEyeOff size={18} />}
                          </button>
                          <span className="f-total">
                            {group.totalSqFt ? formatArea(group.totalSqFt) : `${group.count || group.length} Units`}
                          </span>
                        </div>
                      </div>

                      {openFeatures[group.id] && (
                        <div className="feature-dropdown">
                          {group.children.map(child => (
                            <div key={child.id} className="drop-row">
                              <div className="drop-left">
                                <button 
                                  className={`vis-toggle-sm ${!child.isVisible ? 'hidden' : ''}`}
                                  onClick={() => toggleItemVisibility(category, group.id, child.id)}
                                >
                                  {child.isVisible ? <LuEye size={14} /> : <LuEyeOff size={14} />}
                                </button>
                                <span>{child.type || 'ID'}: {child.id}</span>
                              </div>
                              <div className="drop-right">
                                <button className="del-btn"><LuTrash2 size={12} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </aside>

        <section className="map-view-wrapper">
          <MapContainer center={centerPosition} zoom={15} zoomControl={false} className="main-map">
            <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
            
            {/* RENDER ZONES */}
            {zones.map(zone => zone.isVisible && (
              <Polygon key={zone.id} positions={zone.coords} pathOptions={{ color: zone.color, weight: 2, dashArray: '5, 5', fillOpacity: 0.2 }}>
                <Tooltip sticky>{zone.name}</Tooltip>
              </Polygon>
            ))}

            {/* RENDER FEATURES */}
            {features.multipolygon.map(group => 
              group.isVisible && group.children.map(child => child.isVisible && (
                <Polygon key={child.id} positions={child.coords} pathOptions={{ color: group.color, fillColor: group.color, fillOpacity: 0.4 }}>
                  <Tooltip sticky>{group.name}: {child.id}</Tooltip>
                </Polygon>
              ))
            )}

            {features.multilinestring.map(group => 
              group.isVisible && group.children.map(child => child.isVisible && (
                <Polyline key={child.id} positions={child.coords} pathOptions={{ color: group.color, weight: 4 }}>
                  <Tooltip sticky>{group.name}: {child.id}</Tooltip>
                </Polyline>
              ))
            )}

            {features.points.map(group => 
              group.isVisible && group.children.map(child => child.isVisible && (
                <CircleMarker key={child.id} center={child.coords} pathOptions={{ color: group.color, fillColor: group.color, fillOpacity: 1 }} radius={6}>
                  <Tooltip>Asset: {child.type} ({child.id})</Tooltip>
                </CircleMarker>
              ))
            )}

            <ZoomControl position="bottomright" />
          </MapContainer>
        </section>
      </main>
    </div>
  );
};

export default Report;